import useExtendedFormContext from '@components/form-hook-wrapper/hooks/useExtendedFormContext';
import { Textarea } from '@mis/sushi-tailwind-react';
import { FieldController } from '@model/field-controller';
import React from 'react';
import { Controller, FieldPath, FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export type InputTextareaProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = FieldController<TFieldValues, TName> &
  React.ComponentProps<typeof Textarea> & {
    viewOnly?: boolean;
  };

const InputTextarea = <TFieldValues extends FieldValues = FieldValues, TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>>({
  name,
  rules,
  placeholder,
  viewOnly,
  ...props
}: InputTextareaProps<TFieldValues, TName>): React.ReactElement => {
  const { t } = useTranslation('error');
  const { control, displayErrorMessage } = useExtendedFormContext<TFieldValues>();

  return (
    <Controller
      name={name}
      rules={rules}
      control={control}
      render={({ field, fieldState: { error } }) => {
        return (
          <Textarea
            rows={5}
            {...field}
            {...props}
            readOnly={viewOnly}
            value={field.value === null ? '' : field.value}
            autoComplete="off"
            placeholder={placeholder}
            status={displayErrorMessage && error ? 'error' : undefined}
            message={displayErrorMessage && error ? t(error?.message as string) : undefined}
            data-testid={field.name}
          />
        );
      }}
    />
  );
};

export default InputTextarea;
